import gql from 'graphql-tag';

import { Mutation, Query } from 'core';
import { USER_ERROR_FIELDS } from 'core/errors';
import { FILE_FIELDS } from 'apps/file/gql';

const ORIGIN_BASIC_FRAGMENT = gql`
  fragment origin on Contribution {
    id
    title
    version
    space {
      id
      spaceType
      content {
        id
        slug
      }
    }
  }
`;

const ORIGIN_DETAIL_FRAGMENT = gql`
  ${ORIGIN_BASIC_FRAGMENT}
  fragment originDetail on Contribution {
    ...origin
    project {
      id
      name
      slug
      forkCount
      isStarred
      starCount
      collections {
        id
        slug
      }
      space {
        id
        spaceType
        content {
          id
          slug
        }
      }
      content {
        id
        followersCount
        isFollowed
      }
    }
  }
`;

const OPERATIONS_FRAGMENT = gql`
  fragment operations on Contribution {
    operations {
      id
      opType
      file {
        id
        path
        size
        completed
        permalink
      }
      path
    }
  }
`;

const FILES_FRAGMENT = gql`
  ${ORIGIN_BASIC_FRAGMENT}
  fragment files on Contribution {
    files(basepath: $basepath) {
      id
      file {
        id
        path
        size
        permalink
        completed
        gitHash
      }
      origin {
        ...origin
      }
      isFolder
      dirname
      filename
      lastUpdated
    }
  }
`;

const FILE_DETAIL_FRAGMENT = gql`
  ${ORIGIN_DETAIL_FRAGMENT}
  fragment contribFile on Contribution {
    contribFile(filepath: $filepath) {
      id
      file {
        id
        filename
        mimeType
        path
        size
        fileLastModified
        completed
        url
        permalink
        canDownload
        originalFile {
          id
        }
        private
        space {
          id
          parent {
            id
          }
        }
        conversionGltfjson {
          id
          status
          reason
          errorCode
          viewerSemver
          convertedFile {
            id
          }
        }
      }
      origin {
        ...originDetail
      }
      dirname
      filename
    }
  }
`;

const CONTRIBUTION_FIELDS = gql`
  fragment contributionFields on Contribution {
    id
    title
    description
    status
    version
    dateCreated
    lastUpdated
    zipArchiveUrl
    creatorProfile {
      id
      username
      imageFallbackChar
      avatar {
        id
        path
      }
    }
  }
`;

const CONTRIBUTION_CONNECTION_FIELDS = gql`
  fragment contributionConnectionFields on ContributionConnection {
    pageInfo {
      startCursor
      endCursor
      hasNextPage
    }
    totalCount
    edges {
      cursor
      node {
        id
        title
        description
        status
        version
        zipArchiveUrl
        creatorProfile {
          id
          username
          imageFallbackChar
          avatar {
            id
            path
            url
          }
        }
        dateCreated
        lastUpdated
        commenters {
          id
          username
          avatar {
            id
            path
          }
        }
        content {
          id
          whitelabel
        }
      }
    }
  }
`;

const CONTRIBUTION = gql`
  ${CONTRIBUTION_FIELDS}
  ${USER_ERROR_FIELDS}
  ${OPERATIONS_FRAGMENT}
  query Contribution($projectId: ID, $version: String) {
    project(id: $projectId) {
      result {
        contribution(version: $version) {
          ...contributionFields
          ...operations
        }
        contributions {
          totalCount
        }
        contributors {
          totalCount
        }
      }
      userErrors {
        ...userErrorFields
      }
    }
  }
`;

const CONFLICT_FIELDS = gql`
  ${FILE_FIELDS}
  fragment conflictFields on Conflict {
    id
    path
    localStatus
    local {
      ...fileFields
    }
    remoteStatus
    remote {
      ...fileFields
    }
    remoteContribution {
      id
      title
      version
      lastUpdated
      creatorProfile {
        id
        imageFallbackChar
        username
        avatar {
          id
          path
          url
        }
      }
    }
  }
`;

export class ContributionQuery extends Query {
  static conf = {
    GQL: CONTRIBUTION,
    resultPath: 'project.result',
    errorPath: 'project.result.userErrors',
  };
}

const CONTRIBUTION_FILE_QUERY = gql`
  ${ORIGIN_BASIC_FRAGMENT}
  query ContributionFile(
    $space: String!
    $slug: String!
    $dirname: String!
    $filename: String!
  ) {
    contributionFile(
      space: $space
      slug: $slug
      dirname: $dirname
      filename: $filename
    ) {
      result(
        space: $space
        slug: $slug
        dirname: $dirname
        filename: $filename
      ) {
        id
        file {
          id
          filename
          mimeType
          path
          size
          completed
          fileLastModified
          permalink
        }
        origin {
          ...origin
        }
        dirname
        filename
        isFolder
      }
    }
  }
`;

export class ContributionFileQuery extends Query {
  static conf = {
    GQL: CONTRIBUTION_FILE_QUERY,
    resultPath: 'contributionFile.result',
  };
}

const CONTRIBUTIONS = gql`
  ${CONTRIBUTION_CONNECTION_FIELDS}
  query Contributions(
    $projectId: ID
    $batchSize: Int
    $cursor: String
    $sortBy: String
  ) {
    project(id: $projectId) {
      result {
        id
        contributions(first: $batchSize, after: $cursor, sortBy: $sortBy) {
          ...contributionConnectionFields
        }
      }
    }
  }
`;

export class ContributionsQuery extends Query {
  static conf = {
    GQL: CONTRIBUTIONS,
    resultPath: 'project.result.contributions',
  };
}

const CONTRIBUTORS = gql`
  query Contributors(
    $projectId: ID
    $sortBy: String
    $batchSize: Int
    $cursor: String
  ) {
    project(id: $projectId) {
      result {
        id
        contributors(first: $batchSize, after: $cursor, sortBy: $sortBy) {
          pageInfo {
            startCursor
            endCursor
            hasNextPage
          }
          totalCount
          edges {
            cursor
            node {
              id
              username
              fullName
              imageFallbackChar
              avatar {
                id
                path
                url
              }
              contributions(projectId: $projectId) {
                totalCount
              }
            }
          }
        }
      }
    }
  }
`;

export class ContributorsQuery extends Query {
  static conf = {
    GQL: CONTRIBUTORS,
    resultPath: 'project.result.contributors',
  };
}

const CONTRIBUTION_PAGE_QUERY = gql`
  ${ORIGIN_DETAIL_FRAGMENT}
  ${CONTRIBUTION_FIELDS}
  ${FILES_FRAGMENT}
  ${USER_ERROR_FIELDS}
  ${OPERATIONS_FRAGMENT}
  query Contribution(
    $space: String
    $slug: String
    $version: String
    $basepath: String
  ) {
    project(space: $space, slug: $slug) {
      result {
        id
        slug
        image {
          id
          path
          url
          completed
          private
        }
        space {
          id
          spaceType
          content {
            id
            slug
          }
        }
        inSpace {
          id
          parent {
            id
            spaceType
            content {
              id
              slug
            }
          }
        }
        contribution(version: $version) {
          ...contributionFields
          ...operations
          ...originDetail
          ...files
        }
      }
      userErrors {
        ...userErrorFields
      }
    }
  }
`;

export class ContributionPageQuery extends Query {
  static conf = {
    GQL: CONTRIBUTION_PAGE_QUERY,
    resultPath: 'project.result',
  };
}

export const AUTOCOMPLETE_REPO_FILES = gql`
  query RepoFiles($space: String, $slug: String, $basepath: String) {
    project(space: $space, slug: $slug) {
      result {
        id
        contribution {
          id
          files(basepath: $basepath) {
            id
            filename
            dirname
            isFolder
          }
        }
      }
    }
  }
`;

export class AutocompleteRepositoryFilesQuery extends Query {
  static conf = {
    GQL: AUTOCOMPLETE_REPO_FILES,
    varNames: ['space', 'slug', 'basepath'],
    resultPath: 'project.result.contribution',
  };
}

const PENDING_OPERATIONS_FRAGMENT = gql`
  ${ORIGIN_BASIC_FRAGMENT}
  fragment pendingOperations on Project {
    pendingOperations {
      id
      opType
      path
      oldPath
      file {
        id
        path
        size
        permalink
        filename
        completed
        canDownload
        originalFile {
          id
        }
        conversionGltfjson {
          id
          status
          reason
          errorCode
          viewerSemver
          convertedFile {
            id
          }
        }
      }
      origin {
        ...origin
      }
    }
  }
`;

const CONTRIBUTION_UPSTREAM_FRAGMENT = gql`
  ${FILES_FRAGMENT}
  ${CONTRIBUTION_FIELDS}
  fragment contributionUpstream on Project {
    contributionUpstream {
      ...contributionFields
      ...files
    }
  }
`;

const CONTRIBUTION_VERSION_FRAGMENT = gql`
  ${FILES_FRAGMENT}
  ${CONTRIBUTION_FIELDS}
  ${OPERATIONS_FRAGMENT}
  fragment contributionVersion on Project {
    contribution(version: $version) {
      ...contributionFields
      ...files
      ...operations
    }
  }
`;

const REPOSITORY_FRAGMENT = gql`
  ${CONTRIBUTION_UPSTREAM_FRAGMENT}
  ${CONTRIBUTION_VERSION_FRAGMENT}
  ${PENDING_OPERATIONS_FRAGMENT}
  fragment repository on Project {
    id
    inSpace {
      id
      whichTypes
    }
    ...contributionUpstream
    ...contributionVersion
    ...pendingOperations
    contributionCount
    contributors {
      totalCount
    }
  }
`;

const REPOSITORY_QUERY = gql`
  ${REPOSITORY_FRAGMENT}
  ${USER_ERROR_FIELDS}
  query Repository(
    $space: String
    $slug: String
    $basepath: String
    $version: String
  ) {
    project(space: $space, slug: $slug) {
      result {
        ...repository
      }
      userErrors {
        ...userErrorFields
      }
    }
  }
`;

export class RepoQuery extends Query {
  static conf = {
    GQL: REPOSITORY_QUERY,
    varNames: ['space', 'slug', 'basepath', 'version'],
    resultPath: 'project.result',
    errorPath: 'project.result.userErrors',
  };
}

export const POLL_ZIP_QUERY = gql`
  ${USER_ERROR_FIELDS}
  query RepositoryZip($space: String, $slug: String) {
    project(space: $space, slug: $slug) {
      result {
        id
        contributionUpstream {
          id
          zipArchiveUrl
        }
      }
      userErrors {
        ...userErrorFields
      }
    }
  }
`;

export class PollZipQuery extends Query {
  static conf = {
    GQL: POLL_ZIP_QUERY,
    varNames: ['space', 'slug'],
    resultPath: 'project.result.contributionUpstream',
    errorPath: 'project.userErrors',
  };
}

const CANCEL_CONTRIBUTION_MUTATION = gql`
  ${REPOSITORY_FRAGMENT}
  mutation ResetContribution(
    $resetData: ResetContributionInput
    $basepath: String
    $version: String
  ) {
    resetContribution(resetData: $resetData) {
      project {
        ...repository
      }
    }
  }
`;

export class CancelContributionMutation extends Mutation {
  static conf = {
    GQL: CANCEL_CONTRIBUTION_MUTATION,
    inputName: 'resetData',
    resultPath: 'resetContribution.project',
    errorPath: 'resetContribution.userErrors',
  };
}

const OPERATION_MUTATION = gql`
  ${PENDING_OPERATIONS_FRAGMENT}
  mutation Operation($operationData: OperationInput) {
    operation(operationData: $operationData) {
      project {
        id
        ...pendingOperations
      }
    }
  }
`;

export class OperationMutation extends Mutation {
  static conf = {
    GQL: OPERATION_MUTATION,
    inputName: 'operationData',
    resultPath: 'operation.project',
  };
}

const CONFLICT_RESOLUTION_FRAGMENT = gql`
  ${CONFLICT_FIELDS}
  fragment conflictResolution on Project {
    conflictsParent
    conflicts {
      ...conflictFields
    }
  }
`;

const COMMIT_CONTRIBUTION_MUTATION = gql`
  ${CONFLICT_RESOLUTION_FRAGMENT}
  ${REPOSITORY_FRAGMENT}
  ${USER_ERROR_FIELDS}
  mutation CommitContribution(
    $commitData: CommitInput
    $basepath: String
    $version: String
  ) {
    commit(commitData: $commitData) {
      project {
        ...repository
        ...conflictResolution
      }
      userErrors {
        ...userErrorFields
      }
    }
  }
`;

export class CommitContributionMutation extends Mutation {
  static conf = {
    GQL: COMMIT_CONTRIBUTION_MUTATION,
    inputName: 'commitData',
    resultPath: 'commit.project',
    errorPath: 'commit.userErrors',
  };
}

const CONTRIBUTION_FILE_DETAILS_QUERY = gql`
  ${FILE_DETAIL_FRAGMENT}
  ${PENDING_OPERATIONS_FRAGMENT}
  ${USER_ERROR_FIELDS}
  query ContributionFileDetails(
    $space: String!
    $slug: String!
    $filepath: String!
  ) {
    project(space: $space, slug: $slug) {
      result {
        id
        context {
          id
          parentObj {
            id
            canUpdate
          }
        }
        contributionUpstream {
          id
          ...contribFile
        }
        ...pendingOperations
      }
      userErrors {
        ...userErrorFields
      }
    }
  }
`;

export class ContributionFileDetailsQuery extends Query {
  static conf = {
    GQL: CONTRIBUTION_FILE_DETAILS_QUERY,
    resultPath: 'project.result',
  };
}

const CONTRIBUTION_FILE_VERSION_DETAILS_QUERY = gql`
  ${FILE_DETAIL_FRAGMENT}
  ${USER_ERROR_FIELDS}
  query ContributionFileVersionDetails(
    $space: String!
    $slug: String!
    $filepath: String!
    $version: String
  ) {
    project(space: $space, slug: $slug) {
      result {
        id
        context {
          id
          parentObj {
            id
            canUpdate
          }
        }
        contribution(version: $version) {
          id
          ...contribFile
        }
      }
      userErrors {
        ...userErrorFields
      }
    }
  }
`;

const REPO_CONFLICTS = gql`
  ${CONFLICT_RESOLUTION_FRAGMENT}
  ${USER_ERROR_FIELDS}
  query Conflicts($space: String, $slug: String) {
    project(space: $space, slug: $slug) {
      result {
        id
        ...conflictResolution
      }
      userErrors {
        ...userErrorFields
      }
    }
  }
`;

export class ContributionFileVersionDetailsQuery extends Query {
  static conf = {
    GQL: CONTRIBUTION_FILE_VERSION_DETAILS_QUERY,
    resultPath: 'project.result',
  };
}

export class ConflictsQuery extends Query {
  static conf = {
    GQL: REPO_CONFLICTS,
    resultPath: 'project.result',
  };
}
